import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bot, User2, Wrench, Clock, MapPin, MessageSquare, History, StickyNote, Sparkles, AlertCircle, ChevronDown, ChevronUp, Copy, Loader2, Camera, FileText, Download, X, ShoppingCart, ExternalLink, Check, Search } from "lucide-react";
import { StatusPill, fmtKr, safeNum } from "@/components/dashboard/Primitives";
import { fetchJobConversation, fetchJobFiles, fetchJobFileBlob, fetchJob, findJobSuppliers, selectJobSupplier } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const PURCHASE_STATUS_LABEL = {
    needs_purchase: "🔴 Skal købes",
    ordered: "🟡 Bestilt / på vej",
    in_stock: "🟢 På lager",
};

// Defensiv normalisering: behandl true, "true", 1, "1", "yes" som true.
const toBool = (v) => v === true || v === 1 || v === "true" || v === "1" || v === "yes";

export default function JobDetailModal({ job, detail, open, onClose }) {
    const [showFullTranscript, setShowFullTranscript] = useState(false);
    const [conv, setConv] = useState(null);
    const [convLoading, setConvLoading] = useState(false);
    const [fullJob, setFullJob] = useState(null);
    const jobId = job?.id;
    useEffect(() => {
        if (!open || !jobId) { setConv(null); return; }
        let alive = true;
        setConvLoading(true);
        fetchJobConversation(jobId)
            .then((d) => { if (alive) setConv(d); })
            .catch(() => { if (alive) setConv(null); })
            .finally(() => { if (alive) setConvLoading(false); });
        return () => { alive = false; };
    }, [open, jobId]);
    // Hent det fulde job (autoritativ kilde) så purchase-felterne altid er med,
    // uanset om list-objektet bar dem. List-objektet bruges kun til åbning/loading.
    useEffect(() => {
        if (!open || !jobId) { setFullJob(null); return; }
        let alive = true;
        fetchJob(jobId)
            .then((d) => { if (alive) setFullJob(d); })
            .catch(() => { if (alive) setFullJob(null); });
        return () => { alive = false; };
    }, [open, jobId]);
    if (!job) return null;
    const d = fullJob || detail || job || {};
    const purchaseRequired = toBool(d.purchase_required);
    const customerHasItem = toBool(d.customer_has_item);
    const materials = d.materials || [];
    const matPrice = (m) => safeNum(m.sell_price_inc_vat ?? m.sell_price ?? m.price);
    const matLineTotal = (m) => safeNum(m.sell_total_inc_vat ?? m.sell_total ?? (safeNum(m.qty) * matPrice(m)));
    const materialsTotal = safeNum(d.materials_total_sale_inc_vat) || materials.reduce((s, m) => s + matLineTotal(m), 0);
    const materialsBuyTotal = safeNum(d.materials_total_buy_ex_vat);
    const materialsMargin = safeNum(d.materials_margin_ex_vat);
    const employeesLog = d.employees_log || [];
    // Normalize n8n employee fields → array (supports array, comma/semicolon/newline strings)
    const toNames = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val.map((s) => String(s).trim()).filter(Boolean);
        return String(val).split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    };
    const tilmeldtLog = employeesLog.filter((e) => e.action === "tilmeldt").map((e) => e.employee);
    const frameldtLog = employeesLog.filter((e) => e.action === "frameldt").map((e) => e.employee);
    // Merge n8n data with legacy log, dedupe
    const tilmeldt = Array.from(new Set([...tilmeldtLog, ...toNames(d.assigned_employee_name)])).filter(Boolean);
    const frameldt = Array.from(new Set([...frameldtLog, ...toNames(d.employee_signoff_status)])).filter(Boolean);
    // Time display: prefer n8n's calculated text, fallback to time_hours
    const timeDisplay = d.time_calculated_text || (d.time_hours ? `${safeNum(d.time_hours)} t` : "—");
    const legacyTranscript = d.transcript || [];

    // ==== Voice Intake / Post-Call hydration (priority order per spec) ====
    const isVoiceJob = (d.source || job.source || "") === "voice_elevenlabs";
    // Customer name: hydrated → snapshot → legacy
    const customerName =
        d.customer ||                        // backend hydrates from customer_id
        d.customer_name ||                   // backend field
        d.customer_name_snapshot ||          // job snapshot from voice intake
        job.customer ||
        "";
    // Address: service_address → hydrated customer.address → snapshot
    const buildAddr = () => {
        const svc = d.service_address || d.address_service;
        const pc = d.postal_code || "";
        const city = d.city || "";
        if (svc || pc || city) {
            return [svc, [pc, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
        }
        return d.full_address || d.address || "";
    };
    const displayAddress = buildAddr();

    // Customer message: customer_message → raw_text → voice_transcript → task_description
    const customerMessage =
        d.customer_message ||
        d.customer_original_message ||
        d.raw_text ||
        d.voice_transcript ||
        d.task_description ||
        d.description ||
        "";

    // AI reply: for voice-intake-only jobs, show "Afventer afsluttet opkald"
    let aiReply = d.ai_last_reply || d.ai_response || d.ai_summary || d.voice_transcript_summary || "";
    const awaitingPostCall = isVoiceJob && d.voice_intake_status === "awaiting_post_call";
    if (!aiReply && awaitingPostCall) {
        aiReply = "Afventer afsluttet opkald";
    }

    // Parse chat_messages_json into bubbles (handles string-JSON or already-parsed array)
    let chatMessages = [];
    const raw = d.chat_messages_json;
    if (Array.isArray(raw)) {
        chatMessages = raw;
    } else if (typeof raw === "string" && raw.trim()) {
        try { const parsed = JSON.parse(raw); if (Array.isArray(parsed)) chatMessages = parsed; } catch (_) {}
    }
    // Normalize each message: { role: 'agent'|'user', text, time }
    const bubbles = chatMessages
        .map((m) => {
            if (!m || typeof m !== "object") return null;
            const roleRaw = String(m.role || m.author || m.from || "").toLowerCase();
            const isAgent = ["agent", "assistant", "ai", "bot", "myservicehelper"].some((k) => roleRaw.includes(k));
            return {
                isAgent,
                text: m.text || m.content || m.message || "",
                time: m.time || m.timestamp || "",
            };
        })
        .filter((m) => m && m.text);

    // missing-field detection — for voice-intake jobs, deadline is NOT critical
    const missing = [];
    if (!displayAddress) missing.push("adresse");
    if (!customerMessage) missing.push("kundebesked");
    if (!isVoiceJob && !d.deadline) missing.push("deadline");
    if (!d.priority) missing.push("prioritet");

    // Title fallback: never bare "Opgave"
    const jobTitle = job.task || d.title || d.task || d.task_description || d.job_title || "Ny opgave";

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-slate-500 text-base">{job.id}</span>
                        <span className="text-slate-900">{jobTitle}</span>
                        <StatusPill status={job.status} />
                    </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div className="space-y-2 text-sm">
                        <p className="text-slate-500 text-xs uppercase tracking-wider">Kunde</p>
                        <p className="text-slate-900 font-semibold" data-testid="job-detail-customer">{customerName || "Ukendt kunde"}</p>
                        <p className="flex items-center gap-1.5 text-slate-600" data-testid="job-detail-address"><MapPin className="w-3.5 h-3.5 text-slate-400" />{displayAddress || "Ingen adresse"}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="bg-slate-50 rounded-xl p-3">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400">Prioritet</p>
                            <p className="font-semibold text-slate-900 mt-0.5">{job.priority || "—"}</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400">Deadline</p>
                            <p className="font-semibold text-slate-900 mt-0.5">{job.deadline || "—"}</p>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-3">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />Tid</p>
                            <p className="font-semibold text-slate-900 mt-0.5 font-mono text-xs" data-testid="job-detail-time">{timeDisplay}</p>
                        </div>
                    </div>
                </div>

                {d.description && (
                    <div className="mt-5 bg-slate-50 rounded-xl p-4">
                        <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Opgavebeskrivelse</p>
                        <p className="text-sm text-slate-800">{d.description}</p>
                    </div>
                )}

                {!purchaseRequired && customerHasItem && (
                    <p className="mt-3 text-sm text-emerald-700 font-medium" data-testid="job-customer-has-item">✓ Kunden har varen</p>
                )}

                {/* AI-samtale / Kundehenvendelse — fuld samtale-timeline (P0.7) */}
                <section className="mt-5" data-testid="job-conversation-timeline">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-blue-500" />AI-samtale / Kundehenvendelse
                    </h3>

                    {convLoading ? (
                        <div className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center" data-testid="job-timeline-loading">
                            <Loader2 className="w-4 h-4 animate-spin" /> Henter samtale…
                        </div>
                    ) : (conv?.turns?.length > 0) ? (
                        <div className="bg-slate-50 rounded-xl p-4 max-h-[28rem] overflow-y-auto border border-slate-100 space-y-4" data-testid="job-timeline-turns">
                            {conv.turns.map((t, i) => {
                                const isCustomer = t.role === "customer";
                                const secs = t.time_in_call_secs;
                                const mmss = (secs || secs === 0)
                                    ? `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`
                                    : null;
                                return (
                                    <div key={i} className="flex gap-3" data-testid={`job-turn-${i}`}>
                                        <div className="flex flex-col items-center pt-1">
                                            <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isCustomer ? "bg-slate-200 text-slate-600" : "bg-blue-600 text-white"}`}>
                                                {isCustomer ? <User2 className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                                            </span>
                                            {i < conv.turns.length - 1 && <span className="w-px flex-1 bg-slate-200 mt-1" />}
                                        </div>
                                        <div className="pb-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs font-semibold ${isCustomer ? "text-slate-700" : "text-blue-700"}`}>
                                                    {isCustomer ? "Kunde" : "Noah AI"}
                                                </span>
                                                {mmss && <span className="text-[10px] text-slate-400">{mmss}</span>}
                                            </div>
                                            <p className="text-sm text-slate-800 whitespace-pre-wrap mt-0.5 break-words">{t.message}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : conv?.status === "pending" ? (
                        <div className="bg-slate-50 rounded-xl p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="job-timeline-pending">
                            <Clock className="w-4 h-4 text-slate-400" /> Afventer afsluttet opkald — samtalen vises her, når opkaldet er afsluttet.
                        </div>
                    ) : (
                        // Fallback: no post-call transcript — show the intake summary cards
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="job-timeline-fallback">
                            <div className="bg-slate-50 rounded-xl p-3 text-sm">
                                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 mb-1"><User2 className="w-3 h-3" />Kundens besked</div>
                                <p data-testid="job-customer-message" className="text-slate-800 whitespace-pre-wrap">{customerMessage || <span className="text-slate-400 italic">Ingen besked registreret</span>}</p>
                            </div>
                            <div className="bg-blue-50/60 rounded-xl p-3 text-sm border border-blue-100">
                                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blue-700 mb-1"><Bot className="w-3 h-3" />AI-assistentens svar</div>
                                <p data-testid="job-ai-reply" className="text-slate-800 whitespace-pre-wrap">{aiReply || <span className="text-slate-400 italic">Intet svar registreret</span>}</p>
                            </div>
                        </div>
                    )}

                    {/* AI-resumé (separat — erstatter ikke den fulde samtale) */}
                    {(conv?.summary || d.voice_transcript_summary) && (
                        <div className="mt-3 bg-blue-50/60 rounded-xl p-3 text-sm border border-blue-100" data-testid="job-ai-summary">
                            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-blue-700 mb-1"><Sparkles className="w-3 h-3" />AI-resumé</div>
                            <p className="text-slate-800 whitespace-pre-wrap">{conv?.summary || d.voice_transcript_summary}</p>
                        </div>
                    )}

                    {missing.length > 0 && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-900">
                            <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                            <span>Manglende felter fra AI-samtalen: <strong>{missing.join(", ")}</strong></span>
                        </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">Opgaven blev oprettet automatisk{d.created_at ? ` · ${new Date(d.created_at).toLocaleString("da-DK")}` : ""}.</p>
                </section>

                {(bubbles.length > 0 || d.transcript_display_text || legacyTranscript.length > 0) && (
                    <section className="mt-5" data-testid="job-samtale-section">
                        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                            <MessageSquare className="w-4 h-4 text-slate-400" />Samtale
                            {d.call_started_at && (
                                <span className="text-xs font-normal text-slate-400 ml-1">
                                    · {new Date(d.call_started_at).toLocaleString("da-DK")}
                                    {d.call_duration_seconds ? ` · ${Math.round(d.call_duration_seconds)} sek` : ""}
                                </span>
                            )}
                        </h3>
                        {bubbles.length > 0 ? (
                            <div data-testid="job-chat-bubbles" className="bg-slate-50 rounded-xl p-4 max-h-96 overflow-y-auto space-y-3 border border-slate-100">
                                {bubbles.map((b, i) => (
                                    <div key={i} className={`flex flex-col ${b.isAgent ? "items-start" : "items-end"}`}>
                                        <span className={`text-[11px] font-semibold mb-1 ${b.isAgent ? "text-blue-700" : "text-slate-600"}`}>
                                            {b.isAgent ? "MyServiceHelper" : "Kunde"}
                                        </span>
                                        <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${b.isAgent ? "bg-white border border-slate-200 text-slate-800 rounded-tl-sm" : "bg-blue-600 text-white rounded-tr-sm"}`}>
                                            <p className="whitespace-pre-wrap">{b.text}</p>
                                            {b.time && (
                                                <p className={`text-[10px] mt-1 ${b.isAgent ? "text-slate-400" : "text-blue-100"}`}>{b.time}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : legacyTranscript.length > 0 && !d.transcript_display_text ? (
                            <div className="bg-slate-50 rounded-xl p-3 max-h-64 overflow-y-auto space-y-2">
                                {legacyTranscript.map((t, i) => (
                                    <div key={i} className={`flex ${t.role === "ai" ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${t.role === "ai" ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                                            <p>{t.text}</p>
                                            <p className={`text-[10px] mt-0.5 ${t.role === "ai" ? "text-blue-100" : "text-slate-400"}`}>{t.time}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        {/* Fuld transcript (rå tekst) — altid vises hvis den findes, uanset om der også er chat-bobler */}
                        {d.transcript_display_text && (
                            <div className="mt-2 border border-slate-200 rounded-xl overflow-hidden bg-white">
                                <button
                                    type="button"
                                    data-testid="job-transcript-toggle"
                                    onClick={() => setShowFullTranscript((v) => !v)}
                                    className="w-full flex items-center justify-between px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                                        {showFullTranscript ? "Skjul hele samtalen" : "Vis hele samtalen (transcript)"}
                                        <span className="text-[10px] font-normal text-slate-400">
                                            · {d.transcript_display_text.length.toLocaleString("da-DK")} tegn
                                        </span>
                                    </span>
                                    {showFullTranscript ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                </button>
                                {showFullTranscript && (
                                    <div className="border-t border-slate-100">
                                        <div className="flex items-center justify-end px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                                            <button
                                                type="button"
                                                data-testid="job-transcript-copy"
                                                onClick={() => {
                                                    navigator.clipboard?.writeText(d.transcript_display_text || "");
                                                }}
                                                className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-800 transition-colors"
                                            >
                                                <Copy className="w-3 h-3" />Kopiér
                                            </button>
                                        </div>
                                        <div
                                            data-testid="job-transcript-display"
                                            className="bg-slate-900 text-slate-100 p-4 whitespace-pre-wrap text-xs leading-relaxed font-mono max-h-[60vh] overflow-y-auto"
                                        >
                                            {d.transcript_display_text}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {d.ai_summary && (
                            <div className="mt-2 bg-blue-50/60 border border-blue-100 rounded-xl p-3 text-sm">
                                <div className="text-[11px] uppercase tracking-wider text-blue-700 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" />AI-resume</div>
                                <p className="text-slate-800">{d.ai_summary}</p>
                            </div>
                        )}
                        {d.access_note_private && (
                            <div className="mt-2 bg-amber-50/60 border border-amber-100 rounded-xl p-3 text-sm">
                                <div className="text-[11px] uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1"><StickyNote className="w-3 h-3" />Privat note (kun internt)</div>
                                <p className="text-slate-800 whitespace-pre-wrap">{d.access_note_private}</p>
                            </div>
                        )}
                    </section>
                )}

                {purchaseRequired && !customerHasItem && (
                    <PurchaseBlock d={d} jobId={job.id} onPatch={(patch) => setFullJob((prev) => ({ ...(prev || d), ...patch }))} />
                )}

                <section className="mt-5">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><Wrench className="w-4 h-4 text-slate-400" />Materialer ({materials.length})</h3>
                    {materials.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">Ingen materialer registreret.</p>
                    ) : (
                        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                            <table className="w-full text-sm" data-testid="job-materials-table">
                                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                    <tr><th className="px-3 py-2 text-left">Materiale</th><th className="px-3 py-2 text-right">Antal</th><th className="px-3 py-2 text-right">Pris</th><th className="px-3 py-2 text-right">Sum</th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {materials.map((m, i) => (
                                        <tr key={i} data-testid={`job-material-row-${i}`}>
                                            <td className="px-3 py-2 text-slate-700">
                                                {m.name || "Ukendt"}
                                                {m.material_id ? <span className="ml-2 text-[10px] font-mono text-slate-400">{m.material_id}</span> : null}
                                            </td>
                                            <td className="px-3 py-2 text-right text-slate-600">{safeNum(m.qty)}{m.unit ? ` ${m.unit}` : ""}</td>
                                            <td className="px-3 py-2 text-right text-slate-600">{fmtKr(matPrice(m))}</td>
                                            <td className="px-3 py-2 text-right font-medium text-slate-900">{fmtKr(matLineTotal(m))}</td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td colSpan={3} className="px-3 py-2 text-right text-slate-500">
                                            Total {materialsBuyTotal ? <span className="ml-2 text-xs text-slate-400">(køb {fmtKr(materialsBuyTotal)} · margin {fmtKr(materialsMargin)})</span> : null}
                                        </td>
                                        <td className="px-3 py-2 text-right font-bold text-emerald-600" data-testid="job-materials-total">{fmtKr(materialsTotal)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                <JobDocumentation jobId={job.id} open={open} />

                <section className="mt-5" data-testid="job-detail-employee-status">
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Medarbejdere på opgaven</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1" data-testid="job-detail-tilmeldt-header">Tilmeldt ({tilmeldt.length})</p>
                            <div className="flex flex-wrap gap-1.5" data-testid="job-detail-tilmeldt-list">
                                {tilmeldt.length === 0 ? <span className="text-slate-400 italic" data-testid="job-detail-tilmeldt-empty">Ingen</span> : tilmeldt.map((n, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">{n}</span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-wider text-red-700 font-semibold mb-1" data-testid="job-detail-frameldt-header">Frameldt ({frameldt.length})</p>
                            <div className="flex flex-wrap gap-1.5" data-testid="job-detail-frameldt-list">
                                {frameldt.length === 0 ? <span className="text-slate-400 italic" data-testid="job-detail-frameldt-empty">Ingen</span> : frameldt.map((n, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-xs font-medium">{n}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="mt-5">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><History className="w-4 h-4 text-slate-400" />Historik over opdateringer</h3>
                    {employeesLog.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">Ingen registreringer.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100 bg-white rounded-xl border border-slate-100">
                            {employeesLog.map((e, i) => (
                                <li key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                                    <span className="text-slate-700 font-medium">{e.employee}</span>
                                    <span className="text-slate-500"><span className={`inline-block mr-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${e.action === "frameldt" ? "bg-red-50 text-red-700" : e.action === "tilmeldt" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>{e.action}</span>{e.time}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="mt-5">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><StickyNote className="w-4 h-4 text-slate-400" />Noter ({(d.notes || []).length})</h3>
                    {(d.notes || []).length === 0 ? (
                        <p className="text-sm text-slate-400 italic">Ingen noter.</p>
                    ) : (
                        <ul className="space-y-2">
                            {(d.notes || []).map((n, i) => (
                                <li key={i} className="bg-amber-50/60 border border-amber-100 rounded-xl px-3 py-2 text-sm">
                                    <p className="text-slate-800">{n.text}</p>
                                    <p className="text-xs text-slate-500 mt-1">{n.author || "Ukendt"} · {n.time || ""}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <div className="mt-6 flex justify-end gap-2">
                    <Button data-testid="job-detail-close" onClick={onClose} className="bg-slate-900 hover:bg-slate-800 text-white">Luk</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ==== P0 Specialbestilling: vis vare + find/vælg dansk leverandør ====
function PurchaseBlock({ d, jobId, onPatch }) {
    const [suppliers, setSuppliers] = useState(null);
    const [disclaimer, setDisclaimer] = useState("");
    const [loading, setLoading] = useState(false);
    const [selecting, setSelecting] = useState("");

    const isOrdered = d.purchase_status === "ordered";
    const isInStock = d.purchase_status === "in_stock";
    const selectedSupplier = d.selected_supplier || "";
    const selectedUrl = d.selected_product_url || "";

    const doFind = async () => {
        setLoading(true);
        try {
            const res = await findJobSuppliers(jobId);
            setSuppliers(res.suppliers || []);
            setDisclaimer(res.disclaimer || "");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke hente leverandører.");
        } finally { setLoading(false); }
    };

    const doSelect = async (s) => {
        setSelecting(s.shop_name);
        try {
            await selectJobSupplier(jobId, { shop_name: s.shop_name, product_url: s.search_url });
            onPatch({ selected_supplier: s.shop_name, selected_product_url: s.search_url });
            toast.success(`Valgt leverandør: ${s.shop_name}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke gemme valget.");
        } finally { setSelecting(""); }
    };

    return (
        <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4" data-testid="job-purchase-block">
            <p className="text-sm font-semibold text-slate-900 mb-1">🛒 Specialbestilling</p>
            <p className="text-sm font-medium text-slate-800" data-testid="job-purchase-status">{PURCHASE_STATUS_LABEL[d.purchase_status] || PURCHASE_STATUS_LABEL.needs_purchase}</p>
            {[d.purchase_brand, d.purchase_requested_item].filter(Boolean).length > 0 && (
                <p className="text-sm text-slate-700 mt-0.5">{[d.purchase_brand, d.purchase_requested_item].filter(Boolean).join(" · ")}</p>
            )}
            {d.manufacturer_part_number && <p className="text-xs text-slate-500 mt-0.5">Producentnr.: {d.manufacturer_part_number}</p>}

            {/* På lager → lagerplacering + klar til udførelse */}
            {isInStock && (
                <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-3 space-y-1" data-testid="job-instock-block">
                    {d.inventory_location && (
                        <p className="text-sm text-slate-800" data-testid="job-inventory-location">
                            <span className="font-semibold">Lagerplacering:</span> {d.inventory_location}
                        </p>
                    )}
                    <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1" data-testid="job-ready-badge">
                        <Check className="w-4 h-4" /> Klar til udførelse
                    </p>
                </div>
            )}

            {/* Valgt leverandør (mens der købes) */}
            {selectedSupplier && !isInStock && (
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid="job-selected-supplier">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Valgt leverandør</p>
                    <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="text-sm font-medium text-slate-900">{selectedSupplier}</span>
                        {selectedUrl && (
                            <a href={selectedUrl} target="_blank" rel="noopener noreferrer" data-testid="job-selected-supplier-link"
                               className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800">
                                Åbn butik <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                    </div>
                </div>
            )}

            {/* Handlinger */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
                {!isInStock && (
                    <Button size="sm" variant="outline" onClick={doFind} disabled={loading} data-testid="job-find-suppliers-btn" className="h-8 text-xs">
                        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                        Find leverandører
                    </Button>
                )}
                {!isInStock && (
                    <Link to="/dashboard/indkoebsliste" data-testid="job-go-to-purchase-link">
                        <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white">
                            Gå til indkøb <ExternalLink className="w-3.5 h-3.5 ml-1" />
                        </Button>
                    </Link>
                )}
            </div>

            {/* Leverandør-kort */}
            {suppliers && suppliers.length > 0 && !isInStock && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="job-supplier-cards">
                    {suppliers.map((s, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 p-3 flex flex-col" data-testid={`job-supplier-card-${i}`}>
                            <p className="text-sm font-semibold text-slate-900">{s.shop_name}</p>
                            <p className="text-xs text-slate-600 mt-0.5">{[d.purchase_brand, d.manufacturer_part_number].filter(Boolean).join(" · ")}</p>
                            {s.reason && <p className="text-xs text-slate-500 mt-1 flex-1">{s.reason}</p>}
                            <a href={s.search_url} target="_blank" rel="noopener noreferrer" data-testid={`job-supplier-link-${i}`}
                               className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 mt-2">
                                Åbn butik <ExternalLink className="w-3 h-3" />
                            </a>
                            <Button size="sm" variant={d.selected_supplier === s.shop_name ? "default" : "outline"} onClick={() => doSelect(s)}
                                    disabled={!!selecting} data-testid={`job-supplier-select-${i}`} className="h-7 text-xs mt-2">
                                {selecting === s.shop_name ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                                {d.selected_supplier === s.shop_name ? "Valgt" : "Vælg"}
                            </Button>
                        </div>
                    ))}
                    {disclaimer && <p className="col-span-full text-[11px] text-slate-400 italic mt-0.5">{disclaimer}</p>}
                </div>
            )}
            {suppliers && suppliers.length === 0 && (
                <p className="mt-2 text-xs text-slate-400 italic" data-testid="job-suppliers-empty">Ingen forslag fundet.</p>
            )}
        </section>
    );
}


// ==== Dokumentation: medarbejderens før/efter-billeder + kvitteringer ====
const DOC_GROUPS = [
    { key: "before", label: "Før" },
    { key: "after", label: "Efter" },
    { key: "receipt", label: "Kvitteringer" },
];

function DocThumb({ jobId, file, onOpen }) {
    const [src, setSrc] = useState("");
    const isPdf = file.content_type === "application/pdf";
    useEffect(() => {
        if (isPdf) return undefined;
        let url = "";
        let alive = true;
        fetchJobFileBlob(jobId, file.id).then((b) => { if (alive) { url = URL.createObjectURL(b); setSrc(url); } }).catch(() => {});
        return () => { alive = false; if (url) URL.revokeObjectURL(url); };
    }, [jobId, file.id, isPdf]);
    const openPdf = async () => {
        try { const b = await fetchJobFileBlob(jobId, file.id); const u = URL.createObjectURL(b); window.open(u, "_blank"); }
        catch { /* noop */ }
    };
    return (
        <button type="button" onClick={() => (isPdf ? openPdf() : src && onOpen(src, file.original_filename))}
            data-testid={`owner-job-file-${file.kind}`}
            className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shrink-0 group flex items-center justify-center active:scale-95 transition-transform">
            {isPdf ? (
                <span className="flex flex-col items-center gap-1 text-slate-500"><FileText className="w-7 h-7" /><span className="text-[10px] font-medium">PDF</span></span>
            ) : src ? (
                <img src={src} alt={file.caption || file.kind} className="w-full h-full object-cover" />
            ) : <Loader2 className="w-4 h-4 animate-spin text-slate-300" />}
            <span className="absolute bottom-0 inset-x-0 bg-black/45 text-white text-[9px] py-0.5 text-center flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {isPdf ? <><Download className="w-2.5 h-2.5" />Åbn</> : <>Vis</>}
            </span>
        </button>
    );
}

function JobDocumentation({ jobId, open }) {
    const [files, setFiles] = useState(null);
    const [preview, setPreview] = useState(null);
    useEffect(() => {
        if (!open || !jobId) { setFiles(null); return; }
        let alive = true;
        fetchJobFiles(jobId).then((d) => { if (alive) setFiles(d.files || []); }).catch(() => { if (alive) setFiles([]); });
        return () => { alive = false; };
    }, [open, jobId]);

    const total = (files || []).length;
    return (
        <section className="mt-5" data-testid="job-detail-documentation">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                <Camera className="w-4 h-4 text-slate-400" />Dokumentation — fotos & kvitteringer{files !== null ? ` (${total})` : ""}
            </h3>
            {files === null ? (
                <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
            ) : total === 0 ? (
                <p className="text-sm text-slate-400 italic" data-testid="job-documentation-empty">Ingen fotos eller kvitteringer uploadet af medarbejderen endnu.</p>
            ) : (
                <div className="space-y-3">
                    {DOC_GROUPS.map((g) => {
                        const items = files.filter((f) => f.kind === g.key);
                        if (items.length === 0) return null;
                        return (
                            <div key={g.key} data-testid={`owner-files-group-${g.key}`}>
                                <p className="text-xs font-semibold text-slate-600 mb-1.5">{g.label} <span className="text-slate-400">({items.length})</span></p>
                                <div className="flex gap-2 overflow-x-auto pb-1">
                                    {items.map((f) => <DocThumb key={f.id} jobId={jobId} file={f} onOpen={(src, name) => setPreview({ src, name })} />)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {preview && (
                <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="owner-file-preview">
                    <img src={preview.src} alt={preview.name || "Forhåndsvisning"} className="max-w-full max-h-full object-contain rounded-lg" />
                    <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors"><X className="w-5 h-5" /></button>
                </div>
            )}
        </section>
    );
}
